'use client'

import { usePathname } from 'next/navigation'
import { motion } from 'framer-motion'
import { ArrowRight, ChevronRight, Link as LinkIcon } from 'lucide-react'
import { formatDateNormal, formatImageURL } from '@/utils/util'
import { getReadingTime } from '@/utils/blog'
import { copyToClipboard, getFacebookShareUrl, getTwitterShareUrl, openSharePopup } from '@/utils/share'
import { navigateUrl } from '@/utils/client-request'
import { toast } from '@/lib/toast'
import BlogCard from './BlogCard'

function ShareRow({ title }: { title: string }) {
  const onFacebook = () => openSharePopup(getFacebookShareUrl(window.location.href))
  const onTwitter = () => openSharePopup(getTwitterShareUrl(window.location.href, title))
  const onCopy = async () => {
    const ok = await copyToClipboard(window.location.href)
    if (ok) toast.success('Холбоос хуулагдлаа')
    else toast.error('Хуулж чадсангүй')
  }

  const btnStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.035)',
    border: '1px solid rgba(255,255,255,0.07)',
    color: 'rgba(255,255,255,0.85)',
  }

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onFacebook}
        aria-label="Facebook-р хуваалцах"
        className="inline-flex items-center justify-center size-9 rounded-full transition-colors hover:bg-white/10 hover:text-white"
        style={btnStyle}
      >
        <svg fill="currentColor" viewBox="0 0 24 24" className="size-4">
          <path d="M9 8H6v4h3v12h5V12h3.6l.4-4H14V6.3c0-1 .2-1.3 1.2-1.3H18V0h-3.6C10.6 0 9 1.7 9 4.9V8z" />
        </svg>
      </button>
      <button
        onClick={onTwitter}
        aria-label="X-ээр хуваалцах"
        className="inline-flex items-center justify-center size-9 rounded-full transition-colors hover:bg-white/10 hover:text-white"
        style={btnStyle}
      >
        <svg fill="currentColor" viewBox="0 0 24 24" className="size-3.5">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25h6.81l4.713 6.231 5.467-6.231z" />
        </svg>
      </button>
      <button
        onClick={onCopy}
        aria-label="Холбоос хуулах"
        className="inline-flex items-center justify-center size-9 rounded-full transition-colors hover:bg-white/10 hover:text-white"
        style={btnStyle}
      >
        <LinkIcon className="size-3.5" />
      </button>
    </div>
  )
}

export default function BlogDetail({ blog, related = [], vendor }: { blog: any, related?: any[], vendor?: any }) {
  const pathname = usePathname()
  const readMin = getReadingTime(blog?.content || '')
  const category = blog?.category?.name || blog?.category || ''
  const vendorName = vendor?.name || 'Дэлгүүр'

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeIn' }}
    >
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-10">
        <div className="hidden md:flex items-center gap-1.5 text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
          <a href={navigateUrl('/', pathname)} className="hover:text-white transition-colors">Нүүр</a>
          <ChevronRight className="h-3 w-3" style={{ opacity: 0.5 }} />
          <a href={navigateUrl('/blog', pathname)} className="hover:text-white transition-colors">Журнал</a>
          <ChevronRight className="h-3 w-3" style={{ opacity: 0.5 }} />
          <span className="text-white font-medium line-clamp-1">{blog.title}</span>
        </div>

        <div className="mt-8 max-w-3xl">
          {category && (
            <div
              className="inline-flex items-center gap-2 h-6 px-2.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.18em]"
              style={{ background: 'rgba(255,255,255,0.035)', color: '#a8a8b3', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              {category}
            </div>
          )}
          <h1
            className="mt-5 font-semibold tracking-[-0.03em] leading-[0.95] text-white"
            style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(34px, 7vw, 72px)' }}
          >
            {blog.title}
          </h1>
          {blog?.subtitle && (
            <p className="mt-5 text-[17px] sm:text-[18px] leading-8" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {blog.subtitle}
            </p>
          )}
          <div className="mt-8 flex items-center justify-between gap-4 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center gap-3 text-[12px] tabular-nums" style={{ color: 'rgba(255,255,255,0.6)' }}>
              <span>{formatDateNormal(new Date(blog.createdAt))}</span>
              {readMin > 0 && (
                <>
                  <span style={{ opacity: 0.4 }}>·</span>
                  <span>{readMin} мин унших</span>
                </>
              )}
            </div>
            <ShareRow title={blog.title || ''} />
          </div>
        </div>
      </div>

      {blog?.thumbnail && (
        <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-12">
          <div className="rounded-3xl overflow-hidden aspect-21/9" style={{ border: '1px solid rgba(255,255,255,0.13)' }}>
            <img src={formatImageURL(blog.thumbnail)} alt={blog.title} className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-16">
        <div className="max-w-3xl">
          {blog?.content ? (
            <div className="dark-editorial-prose" dangerouslySetInnerHTML={{ __html: blog.content }} />
          ) : (
            <p className="text-[14px]" style={{ color: 'rgba(255,255,255,0.55)' }}>Энэ нийтлэлд агуулга алга байна.</p>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-20 pt-12" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-end justify-between mb-7 gap-4">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-2" style={{ color: '#a8a8b3' }}>Холбоотой</div>
              <h2
                className="text-2xl sm:text-[28px] font-semibold tracking-[-0.02em] leading-none text-white"
                style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}
              >
                Үргэлжлүүлэн уншаарай.
              </h2>
            </div>
            <a
              href={navigateUrl('/blog', pathname)}
              className="text-[13px] font-medium inline-flex items-center gap-1.5 px-4 h-9 rounded-full shrink-0"
              style={{ color: 'rgba(255,255,255,0.9)', background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              Бүгдийг харах <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-10">
            {related.slice(0, 3).map((p, i) => <BlogCard key={p.id} blog={p} index={i} />)}
          </div>
        </div>
      )}

      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-20">
        <a
          href={navigateUrl('/product', pathname)}
          className="block rounded-2xl p-6 sm:p-8 transition-colors hover:bg-white/5 max-w-3xl mx-auto"
          style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <div className="flex items-center justify-between gap-6 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>{vendorName}</div>
              <div className="mt-2 text-[18px] sm:text-[20px] font-semibold text-white leading-snug" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
                Бүтээгдэхүүн үзэх үү?
              </div>
              <p className="mt-1 text-[13px]" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Манай шинэ бүтээгдэхүүнүүдтэй танилцана уу.
              </p>
            </div>
            <span
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full text-[13px] font-medium shrink-0"
              style={{ background: 'white', color: '#0a0a0c' }}
            >
              Дэлгүүр үзэх <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </a>
      </div>
    </motion.div>
  )
}
